import { parsePhoneNumberFromString } from 'libphonenumber-js';
import type { PatientData } from 'types';

/**
 * The `/Patient/?...` search bundle as these four list screens consume it.
 *
 * `FHIRFetcher` resolves `undefined` without calling the API when its endpoint is
 * empty, and SWR's `onSuccess` is handed that value verbatim — every one of these
 * handlers opens with a `data &&` guard for exactly that reason.
 *
 * `PatientData` rather than `fhir4.Bundle` because the handlers read
 * `entry[i].resource.id` unconditionally: `Bundle.entry` and `BundleEntry.resource`
 * are both `0..1` in FHIR, whereas `PatientData` states the narrower shape the
 * `/fhir` proxy actually returns for this query and that `MemberCard` requires.
 */
export type PatientSearchResult = PatientData | undefined;

export const normalizeSearchString = (searchString: string): string => {
  const trimmed = searchString?.trim() ?? '';
  if (!trimmed) return '';

  try {
    const phone = parsePhoneNumberFromString(trimmed, 'US');
    
    if (phone && phone.isPossible() && !/[a-zA-Z]/.test(trimmed)) {
      const normalized = phone.nationalNumber;
      const dashedVersion = `${normalized.slice(0, 3)}-${normalized.slice(3, 6)}-${normalized.slice(6)}`;
      const parenthsesizedVersion = `(${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6)}`;
      return `${normalized},${dashedVersion},${parenthsesizedVersion}`;
    }
  } catch (err) {
    console.debug('phone parsing failed:', err);
  }

  return trimmed;
};

export const buildSearchParam = (normalizedSearchString: string): string => {
  return normalizedSearchString ? `&_content:contains=${encodeURIComponent(normalizedSearchString)}` : '';
};

export const buildEligibilityParam = (selectedEligibilityGroup?: string | null): string => {
  return selectedEligibilityGroup
    ? `&identifier=https://projectwell.io/fhir/identifiers/eligibility-group|${selectedEligibilityGroup}`
    : '';
};

export const commonSWROptions = {
  shouldRetryOnError: false,
  revalidateOnFocus: false,
  keepPreviousData: true,
};
